import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';

import { getPopupRegistration, openPopup, closePopup, Priority } from 'app/PopupHolder';
import ListManagementField from 'components/ListManagementField';
import CheckboxItem from 'components/Checkbox';
import Tooltip from '../Tooltip';
import SVG from 'components/base/SVG';
import { useToast } from '../ToastMessage/ToastManager';
import { contactGroupPath } from 'app/routes';
import { selectInvestorGroups } from 'data/contacts';
import { saveInvestorGroup, materializeInvestorContacts } from 'data/investors';
import { saveInvestorSnapshots } from 'data/investors/investorProfileService';
import { toInvestorContactRequest } from 'data/investors/investorUtils';
import InvestorSkipTraceModal from '../InvestorSkipTraceModal';
import infoGray from 'assets/images/info-gray.svg';

import css from '../AddToMarketingListModal/style.scss';

const BODY_CLASS_NAME = 'bodyModal';

// Same guard useInvestorProfileData.js uses before calling this same
// save-snapshot endpoint -- only a full search-result object is worth
// persisting; a thin `{id, saved}` shape (re-adding an investor removed
// via the still-stubbed ATAI-186 remove flow, see useSaveInvestor.js)
// would just overwrite a real snapshot with near-nothing.
const hasRichPayload = (investor) =>
  Boolean(investor?.metrics || investor?.mailingAddresses || investor?.contacts);

// Dedicated "Add to Investor List" modal (ATAI-180 / S10, PR #1662 on
// propstream-be). A separate component rather than reusing the generic
// AddToMarketingListModal — that one's save flow goes through a shared
// (name, onComplete, saveOptions) callback contract built for the
// property/contact save paths; getting the investor payload built and the
// group id round-tripped through that indirection was hard to verify. This
// component owns the whole save flow directly: builds the
// InvestorContactRequest[] payload, calls the real
// PUT .../new/investor-groups endpoint, and handles the toast/navigation
// itself — nothing routed through a generic caller-supplied callback.
//
// Props: `investors` — the raw investor objects selected on the Search page
// (the shape investorUtils.toInvestorContactRequest expects). `onSaved` —
// optional, called (with no args) right after a successful save, so a caller
// like the card/profile bookmark can flip its own local "saved" state.
const AddToInvestorListModal = ({ investors = [], closePopup, history, onSaved }) => {
  const [selectedList, setSelectedList] = useState(null);
  const [skipTrace, setSkipTrace] = useState(false);
  const [saving, setSaving] = useState(false);
  const modalRef = useRef(null);
  const dispatch = useDispatch();
  const groups = useSelector(selectInvestorGroups);
  const showToast = useToast();

  const handleClose = () => {
    document.body.classList.remove(BODY_CLASS_NAME);
    closePopup?.();
  };

  useEffect(() => {
    document.body.classList.add(BODY_CLASS_NAME);
    const handleKeyDown = (e) => e.key === 'Escape' && handleClose();

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.classList.remove(BODY_CLASS_NAME);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!selectedList || saving) return;
    const isCreated = !selectedList.id;

    const payload = {
      investors: investors.map(toInvestorContactRequest),
      ...(isCreated
        ? { name: selectedList.name || selectedList }
        : { groupId: Number(String(selectedList.id).replace(/^\D+/, '')) }),
    };

    setSaving(true);

    // Materialize every investor into investor-service's own store (ATAI-113
    // / S7) before adding them to the list, one batched call -- the endpoint
    // takes a `snapshots` array and InvestorSnapshotService.saveSnapshots
    // already loops over the whole thing in a single transaction, so N
    // investors don't need N requests. The only other caller of this
    // endpoint is useInvestorProfileData.js, gated on the Investor Profile
    // modal actually being opened first (Search's "View Details") -- an
    // investor added straight from the search card's bookmark, or via the
    // grid's bulk "Add to Investor List" action, never goes through that
    // path. Without this, GET /ps-core/investors/{id} 404s the first time
    // someone opens it from the Contacts Investor List (which only ever has
    // the thin {id, contactId} shape to open with, nothing to materialize
    // from at that point -- see Contacts/InvestorProfileRoute.js). Best
    // effort, same as that hook: a failed materialization here just means
    // the profile falls back to whatever thin data is on screen, same as
    // any other 404 -- it must not block the actual list save.
    const richInvestors = investors.filter(hasRichPayload);

    if (richInvestors.length) {
      try {
        await saveInvestorSnapshots(richInvestors);
      } catch (error) {
        console.warn('Could not materialize investor snapshots:', error);
      }
    }

    dispatch(
      saveInvestorGroup(
        payload,
        ({ response }) => {
          setSaving(false);

          if (!response) {
            showToast('Something went wrong. Please try again.', 'error');
            handleClose();
            return;
          }

          const savedGroup = response.find((g) => g.modified);
          const listName = savedGroup?.name || selectedList.name || selectedList;
          const count = investors.length;

          showToast(
            `${count} ${count === 1 ? 'investor' : 'investors'} saved to Investor List "${listName}"`,
            'success',
            savedGroup?.id && {
              label: 'View Details',
              onClick: () => history.push(contactGroupPath(savedGroup.id)),
            }
          );

          // Skip Trace checkbox chaining (ATAI-115 / S9): saveInvestorGroup
          // already materialized these investors as a side effect, but its
          // own response doesn't hand back per-investor contact ids -- a
          // second materialize call is the reliable way to get them (it
          // dedupes on batchdataInvestorId, so this just reads back the ids
          // it already assigned, no duplicate contacts created).
          if (skipTrace) {
            dispatch(
              materializeInvestorContacts(
                { investors: investors.map(toInvestorContactRequest) },
                ({ response: materializeResponse }) => {
                  const ids = (materializeResponse?.results || [])
                    .map((result) => result.contactId)
                    .filter((id) => id != null);

                  if (ids.length) {
                    dispatch(
                      InvestorSkipTraceModal.open({
                        contactIds: ids,
                        investors,
                        lockedGroup: { id: savedGroup?.id, name: listName },
                      })
                    );
                  }

                  onSaved?.();
                  handleClose();
                },
                () => {
                  console.warn('Could not materialize investors for skip trace chaining');
                  onSaved?.();
                  handleClose();
                }
              )
            );
            return;
          }

          onSaved?.();
          handleClose();
        },
        () => {
          // afterError — the CALL_API middleware never called this before this
          // fix (only afterSuccess was wired), so a failed save left `saving`
          // stuck true forever with no feedback and the modal never closed.
          setSaving(false);
          showToast('Something went wrong. Please try again.', 'error');
          handleClose();
        }
      )
    );
  };

  const handleCancel = () => {
    handleClose();
  };

  return (
    <div className={css.modalOverlay}>
      <div className={css.modalWrapper}>
        <div ref={modalRef} className={classNames(css.modalBox, 'modal-content')}>
          <div className={css.modal_header}>
            <h3 className={css.modal_title}>Add to Investor List</h3>
          </div>

          <div className={css.modal_body}>
            <ListManagementField
              lists={groups}
              onSelect={(list) => setSelectedList(list)}
              onCreate={(name) => setSelectedList({ name })}
              placeholder="Select or Type to Create a New List"
            />
            <div className={css.checkbox_row}>
              <CheckboxItem
                label="Skip Trace Selected Investors"
                checked={skipTrace}
                onChange={() => setSkipTrace(!skipTrace)}
                labelClassName={css.checkBoxLabelClassName}
              />
              <div className={css.info_wrapper}>
                <Tooltip
                  title="Skip Trace"
                  description="Skip Tracing finds phone numbers and emails for your prospects so you can call, text, or email them using PropStream's marketing tools."
                  position="bottom"
                  bgColor="#128292"
                  width={312}
                  xOffset={125}
                  yOffset={-10}
                  arrowOffset={30}
                >
                  <img className={css.clsInfoIconImg} src={infoGray} alt="info" />
                </Tooltip>
              </div>
            </div>
          </div>

          <div className={css.modal_footer}>
            <button
              type="button"
              className={classNames(css.btn, css.btn_secondary, { [css.disabledBtn]: saving })}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="button"
              className={classNames(css.btn, css.btn_primary, {
                [css.disabledBtn]: !selectedList || saving,
              })}
              onClick={handleSave}
              disabled={!selectedList || saving}
            >
              {saving ? <SVG icon="spinner" className={css.spinner} /> : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddToInvestorListModalWithRouter = withRouter(AddToInvestorListModal);

const registrationId = getPopupRegistration(AddToInvestorListModalWithRouter);
AddToInvestorListModalWithRouter.open = (props = {}) =>
  openPopup(registrationId, { priority: Priority.MEDIUM, ...props });
AddToInvestorListModalWithRouter.close = () => closePopup({ popup: registrationId });

export default AddToInvestorListModalWithRouter;
